/* @flow */
// https://github.com/benwiley4000/react-responsive-audio-player/blob/master/src/index.js
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import styles from './styles.module.scss';
import placeholderImg from '../../assets/images/protones-placeholder-1.png';
import playlistImg from '../../assets/images/playlist.svg';
import closeImg from '../../assets/images/close.svg';
import IconButton from '../../components/IconButton';
import ErrorAwareImage from '../../components/ErrorAwareImage';
import stringUtils from '../../utils/string';

import type { PlaybackStatus, Translate } from '../../types';

type PlayerTrack = {
  id: number,
  artist: string,
  title: string,
  albumArtUrl: ?string,
  url: string,
};

type Props = {
  tracks: Array<PlayerTrack>,
  requestedPlaybackStatus: PlaybackStatus,
  initialTrackIndex: ?number,
  isPlayerPlaylistVisible?: boolean,
  onCurrentTrackChange: (trackIndex: number, trackId: number) => mixed,
  onPlaybackStatusChange: (status: PlaybackStatus) => mixed,
  onShowPlaylistClick: () => mixed,
  t: Translate,
};

type State = {
  activeTrackIndex: number,
  playing: boolean,
  elapsedTime: number,
  volumeLevel: number,
  isAudioMuted: boolean,
};

class Player extends React.PureComponent<Props, State> {
  // API HTML Audio
  audio: HTMLAudioElement = document.createElement('audio'); // L'utente sta muovendo la progress bar?
  seekInProgress: boolean = false; // Indice traccia corrente
  currentTrackIndex: number = 0; // Listener vari per progress bar
  seekReleaseListener = (event: any) => this._seek(event);
  resizeListener = () => this._fetchAudioProgressBoundingRect(); // Listener vari per player audio
  audioPlayListener = () => this.setState({ playing: true });
  audioPauseListener = () => this.setState({ playing: false });
  audioEndedListener = () => this._skipTo('NEXT');
  audioStalledListener = () => this._pauseAudio();
  audioTimeUpdateListener = () => this._handleTimeUpdate();
  audioLoadedMetadataListener = () => this.setState({ activeTrackIndex: this.currentTrackIndex }); // Refs per la progress bar
  audioProgressContainer: any = null;
  audioProgressBoundingRect: any = null;

  get initialState(): State {
    return {
      activeTrackIndex: this.props.initialTrackIndex || -1, // Sarà uguale a this.currentTrackIndex dopo il caricamento dei metadati
      playing: false,
      elapsedTime: 0, // Tempo passato dall'inizio di questa traccia, in secondi
      volumeLevel: 1,
      isAudioMuted: false,
    };
  }

  state = this.initialState;

  componentDidMount() {
    document.addEventListener('touchend', this.seekReleaseListener);
    window.addEventListener('mouseup', this.seekReleaseListener);
    window.addEventListener('resize', this.resizeListener);
    this.resizeListener();
    this.audio.preload = 'metadata';
    this.audio.addEventListener('play', this.audioPlayListener);
    this.audio.addEventListener('pause', this.audioPauseListener);
    this.audio.addEventListener('ended', this.audioEndedListener);
    this.audio.addEventListener('stalled', this.audioStalledListener);
    this.audio.addEventListener('timeupdate', this.audioTimeUpdateListener);
    this.audio.addEventListener('loadedmetadata', this.audioLoadedMetadataListener);
    if (this.props.tracks && this.props.tracks.length) {
      this._updateSource();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('touchend', this.seekReleaseListener);
    window.removeEventListener('mouseup', this.seekReleaseListener);
    window.removeEventListener('resize', this.resizeListener);
    this.audio.removeEventListener('play', this.audioPlayListener);
    this.audio.removeEventListener('pause', this.audioPauseListener);
    this.audio.removeEventListener('ended', this.audioEndedListener);
    this.audio.removeEventListener('stalled', this.audioStalledListener);
    this.audio.removeEventListener('timeupdate', this.audioTimeUpdateListener);
    this.audio.removeEventListener('loadedmetadata', this.audioLoadedMetadataListener);
    this.audio.pause();
  }

  componentWillReceiveProps(nextProps: Props) {
    // TODO: Se sono state cambiate le tracce resetto lo stato del player
    const nextTracks = nextProps.tracks;
    if (!nextTracks || !nextTracks.length) {
      if (this.audio) {
        this.audio.src = '';
      }
      this.currentTrackIndex = 0;
      return this.setState(this.initialState);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { initialTrackIndex } = this.props;
    // if (this.audio && this.currentTrackIndex === -1) {
    //   this._skipToNextTrack();
    // }
    const nextTrackIndex =
      initialTrackIndex != null ? initialTrackIndex : this.props.tracks.length - 1;
    const prevTrackIds = prevProps.tracks.map(t => t.id);
    const currTrackIds = this.props.tracks.map(t => t.id);
    if (this.props.tracks.length && !_.isEqual(prevTrackIds, currTrackIds)) {
      this.playTrack(nextTrackIndex);
    } else if (this.props.requestedPlaybackStatus !== prevProps.requestedPlaybackStatus) {
      switch (this.props.requestedPlaybackStatus) {
        case 'PLAYING':
          if (this.currentTrackIndex !== nextTrackIndex) {
            this.playTrack(nextTrackIndex);
          } else {
            this._playAudio();
          }
          break;
        case 'PAUSED':
          this._pauseAudio();
          break;
        default:
          return;
      }
    }
  }

  // Public
  playTrack = (activeTrackIndex: number) => {
    this.currentTrackIndex = activeTrackIndex;
    this.setState({ activeTrackIndex: -1, elapsedTime: 0 }, () => {
      this._updateSource();
      this._playAudio();
    });
  };

  _playAudio = () => {
    if (!this.audio || !this.props.tracks || !this.props.tracks.length) {
      return;
    }
    try {
      this.audio.play();
      this.props.onPlaybackStatusChange('PLAYING');
    } catch (error) {
      this.props.onPlaybackStatusChange('ERROR');
      console.error(error);
      console.warn(`Audio playback failed at ${new Date().toLocaleTimeString()}`);
    }
  };

  _pauseAudio = () => {
    if (!this.audio) {
      return;
    }
    this.audio.pause();
    this.props.onPlaybackStatusChange('PAUSED');
  };

  _togglePlay = () => {
    if (this.state.playing) {
      this._pauseAudio();
    } else {
      this._playAudio();
    }
  };

  _setVolume = (volumeLevel: number) => {
    if (!this.audio || isNaN(volumeLevel) || volumeLevel < 0 || volumeLevel > 1) {
      return;
    }
    this.audio.volume = volumeLevel;
    this.setState({ volumeLevel });
  };

  _toggleMute = () => {
    if (!this.audio) {
      return;
    }
    this.audio.muted = !this.state.isAudioMuted;
    this.setState({ isAudioMuted: !this.state.isAudioMuted });
  };

  _skipTo = (newTrackPosition: 'NEXT' | 'PREV') => {
    if (!this.audio) {
      return;
    }
    this._pauseAudio();
    if (!this.props.tracks || !this.props.tracks.length) {
      return;
    }
    let newIndex = 0;
    if (newTrackPosition === 'NEXT') {
      newIndex = this.currentTrackIndex + 1;
      if (newIndex >= this.props.tracks.length) {
        return;
      }
    } else if (newTrackPosition === 'PREV') {
      newIndex = this.currentTrackIndex - 1;
      if (newIndex < 0) {
        newIndex = 0;
      }
    }
    this.playTrack(newIndex);
  };

  _updateSource = () => {
    this.props.onCurrentTrackChange(
      this.currentTrackIndex,
      this.props.tracks[this.currentTrackIndex].id
    );
    this.audio.src = this.props.tracks[this.currentTrackIndex].url;
  };

  _fetchAudioProgressBoundingRect = () => {
    this.audioProgressBoundingRect = this.audioProgressContainer.getBoundingClientRect();
  };

  _handleTimeUpdate = () => {
    if (!this.seekInProgress && this.audio) {
      this.setState({ elapsedTime: this.audio.currentTime });
    }
  };

  _adjustDisplayedTime = (event: any) => {
    if (!this.props.tracks || !this.props.tracks.length) {
      return;
    } // Make sure we don't select stuff in the background while seeking
    if (event.type === 'mousedown' || event.type === 'touchstart') {
      this.seekInProgress = true;
    } else if (!this.seekInProgress) {
      return;
    }
    // We don't want mouse handlers to receive the event after touch handlers if we're seeking.
    event.preventDefault();
    const boundingRect = this.audioProgressBoundingRect;
    const isTouch = event.type.slice(0, 5) === 'touch';
    const pageX = isTouch ? event.targetTouches.item(0).pageX : event.pageX; // $FlowFixMe
    const position = pageX - boundingRect.left - document.body.scrollLeft;
    const containerWidth = boundingRect.width;
    const progressPercentage = position / containerWidth;
    this.setState({
      elapsedTime: progressPercentage * this.audio.duration,
    });
  };

  _seek = (event: any) => {
    // Activated when the user lets go of the mouse
    if (!this.seekInProgress) {
      return;
    }
    // We don't want mouse handlers to receive the event after touch handlers if we're seeking.
    event.preventDefault();
    this.seekInProgress = false;
    if (isNaN(this.state.elapsedTime)) {
      return;
    }
    this.audio.currentTime = this.state.elapsedTime;
  };

  render() {
    const { t } = this.props;
    // Testo mostrato sul player
    const currentTrack =
      this.props.tracks && this.state.activeTrackIndex >= 0
        ? this.props.tracks[this.state.activeTrackIndex]
        : null;
    const displayText = currentTrack
      ? `${currentTrack.artist} - ${currentTrack.title}`
      : t('NO_TRACK_TO_PLAY');

    // Durata traccia mostrata sul player
    const duration = this.audio && this.audio.duration;
    const totalTime = stringUtils.prettifyTime(duration);
    const elapsedTime = stringUtils.prettifyTime(this.state.elapsedTime);

    // Lunghezza progress bar
    const progressBarWidth = `${(this.state.elapsedTime / duration) * 100}%`;

    // Immagine di copertina
    const albumArt = currentTrack ? currentTrack.albumArtUrl : placeholderImg;

    // Immagine del toggle playlist
    const playlistToggleImg = this.props.isPlayerPlaylistVisible ? closeImg : playlistImg;

    // a
    const containerClassname =
      this.props.tracks && this.props.tracks.length > 0
        ? styles.container
        : styles.containerDisabled;

    const volumeIcon = this.state.isAudioMuted
      ? 'volume_off'
      : this.state.volumeLevel === 0
      ? 'volume_mute'
      : this.state.volumeLevel < 0.5
      ? 'volume_down'
      : 'volume_up';

    return (
      <div className={containerClassname}>
        <ErrorAwareImage src={albumArt} className={styles.albumArt} alt={t('ALBUM_COVER')} />
        <div className={styles.controls}>
          <IconButton
            icon={'skip_previous'}
            tooltip={t('PREVIOUS_TRACK')}
            onClick={() => this._skipTo('PREV')}
          />
          <IconButton
            icon={this.state.playing ? 'pause' : 'play'}
            tooltip={this.state.playing ? t('PLAYING') : t('PAUSED')}
            onClick={this._togglePlay}
          />
          <IconButton
            icon={'skip_next'}
            tooltip={t('NEXT_TRACK')}
            onClick={() => this._skipTo('NEXT')}
          />
        </div>
        <div
          className={styles.progressContainer}
          ref={ref => (this.audioProgressContainer = ref)}
          onMouseDown={this._adjustDisplayedTime}
          onMouseMove={this._adjustDisplayedTime}
          onTouchStart={this._adjustDisplayedTime}
          onTouchMove={this._adjustDisplayedTime}
        >
          <div className={styles.progressBar} style={{ width: progressBarWidth }} />
          <div className={styles.progressOverlay}>
            <p draggable={'false'}>{displayText}</p>
            <p draggable={'false'}>
              <span className={styles.mono}>{elapsedTime}</span>
              {' / '}
              <span className={styles.mono}>{totalTime}</span>
            </p>
          </div>
        </div>
        <div className={styles.volume}>
          <IconButton
            icon={volumeIcon}
            tooltip={t(this.state.isAudioMuted ? 'UNMUTE_VOLUME' : 'MUTE_VOLUME')}
            onClick={this._toggleMute}
          />
          <input
            className={styles.volumeSlider}
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={this.state.volumeLevel}
            onChange={event => this._setVolume(parseFloat(event.target.value))}
          />
        </div>
        <div className={styles.rightContainer}>
          <ErrorAwareImage
            src={playlistToggleImg}
            onClick={this.props.onShowPlaylistClick}
            alt={t('SHOW_PLAYLIST')}
          />
        </div>
      </div>
    );
  }
}

const TranslatedPlayer = withTranslation()(({ forwardedRef, ...restProps }) => (
  <Player ref={forwardedRef} {...restProps} />
));

const ReferencedPlayer = React.forwardRef((props, ref) => {
  return <TranslatedPlayer {...props} forwardedRef={ref} />;
});

export default ReferencedPlayer;
